import { Box, Text, alpha, styled } from '@ltvco/refresh-lib/theme';
import Article from '@mui/icons-material/Article';
import AttachMoney from '@mui/icons-material/AttachMoney';
import CalendarToday from '@mui/icons-material/CalendarToday';

import { resolveDeadlineStatLine } from './deadlineClaimLabel';
import { StatValue } from './reportTypography';

const StatStrip = styled(Box)({
  display: 'grid',
  gap: '19px',
  paddingTop: 0,
});

const StatCard = styled(Box)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1, 0.5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  backgroundColor:
    theme.palette.mode === 'light' ? '#f8f8f8' : theme.palette.grey[900],
}));

const StatLabelRow = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '4px',
  width: '100%',
});

const StatLabel = styled(Text)(({ theme }) => ({
  fontFamily: "'Lato', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: '18px',
  letterSpacing: '0.16px',
  color: theme.palette.text.primary,
}));

export function ReportStatStrip({
  deadlineAbsolute,
  deadlineRelative,
  payout,
  proof,
  alwaysThreeColumns = false,
}: {
  deadlineAbsolute?: string;
  deadlineRelative?: string;
  payout?: string;
  proof?: string;
  alwaysThreeColumns?: boolean;
}) {
  const deadlineLine = resolveDeadlineStatLine(
    deadlineAbsolute,
    deadlineRelative
  );
  const payoutLine = payout?.trim() || '';
  const proofLine = proof?.trim() || '';

  const showDeadline = Boolean(deadlineLine);
  const showPayout = Boolean(payoutLine);
  const showProof = Boolean(proofLine);

  if (!alwaysThreeColumns && !showDeadline && !showPayout && !showProof) {
    return null;
  }

  const displayDeadline = deadlineLine || (alwaysThreeColumns ? '—' : '');
  const displayPayout = payoutLine || (alwaysThreeColumns ? '—' : '');
  const displayProof = proofLine || (alwaysThreeColumns ? '—' : '');

  const renderDeadline = alwaysThreeColumns || showDeadline;
  const renderPayout = alwaysThreeColumns || showPayout;
  const renderProof = alwaysThreeColumns || showProof;

  const columnCount =
    Number(renderDeadline) + Number(renderPayout) + Number(renderProof);

  return (
    <StatStrip
      sx={{
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
      }}
    >
      {renderDeadline ? (
        <StatCard>
          <StatLabelRow>
            <CalendarToday
              sx={(theme) => ({
                fontSize: 16,
                color: theme.palette.primary.main,
              })}
              aria-hidden
            />
            <StatLabel sx={{ display: 'inline' }}>Deadline</StatLabel>
          </StatLabelRow>
          <StatValue
            sx={(theme) => ({
              display: 'block',
              width: '100%',
              textAlign: 'center',
              color: alpha(theme.palette.text.primary, 0.87),
            })}
          >
            {displayDeadline}
          </StatValue>
        </StatCard>
      ) : null}
      {renderPayout ? (
        <StatCard>
          <StatLabelRow>
            <AttachMoney
              sx={(theme) => ({
                fontSize: 16,
                color: theme.palette.primary.main,
              })}
              aria-hidden
            />
            <StatLabel sx={{ display: 'inline' }}>Payout</StatLabel>
          </StatLabelRow>
          <StatValue
            sx={(theme) => ({
              display: 'block',
              width: '100%',
              textAlign: 'center',
              color: alpha(theme.palette.text.primary, 0.87),
            })}
          >
            {displayPayout}
          </StatValue>
        </StatCard>
      ) : null}
      {renderProof ? (
        <StatCard>
          <StatLabelRow>
            <Article
              sx={(theme) => ({
                fontSize: 16,
                color: theme.palette.primary.main,
              })}
              aria-hidden
            />
            <StatLabel sx={{ display: 'inline' }}>Proof</StatLabel>
          </StatLabelRow>
          <StatValue
            sx={(theme) => ({
              display: 'block',
              width: '100%',
              textAlign: 'center',
              color: alpha(theme.palette.text.primary, 0.87),
            })}
          >
            {displayProof}
          </StatValue>
        </StatCard>
      ) : null}
    </StatStrip>
  );
}
